import React from 'react';
import StandardAd from './Ad';
import BlinkAd from './BlinkAd';
import CmsArticle from './CmsArticle';
import Tracking from '../../tracking';
import { Ads as TroikaAds } from '@finn-no/troika-react-ads';
import NativeAd from './NativeAd';
import { DeviceType } from '@finn-no/displayads-client';
import classNames from 'classnames';
import JobAd from './JobAd';
import GoogleAd from './GoogleAd';

type AdsProps = {
    tracking: Tracking;
    deviceType: DeviceType;
    items: import('../../types').Recommendation[];
    headingLevel: 1 | 2 | 3 | 4 | 5;
    renderActions: ({
        adId,
        itemType,
    }: {
        adId: string;
        itemType: string;
    }) => React.ReactNode;
    url?: string;
    showOverflowMenu?: boolean;
    product?: string;
    singleColumn?: boolean;
} & React.ComponentProps<typeof TroikaAds> &
    Pick<
        React.ComponentProps<typeof StandardAd>,
        'imagesCdnUrl' | 'trackAdClick'
    > &
    Pick<React.ComponentProps<typeof BlinkAd>, 'position'>;

const Ads: React.FC<AdsProps> = (props) => {
    const {
        items,
        tracking,
        trackAdClick,
        imagesCdnUrl,
        renderActions = () => null,
        position,
        headingLevel,
        url,
        showOverflowMenu,
        product,
        singleColumn
    } = props;

    const showNewJobLayout_V2 =
        position.split('_')[0] === 'job-object' ||
        position.split('_')[0] === 'job-browse';

    return (
        <TroikaAds
            liquid={(singleColumn) ? undefined : "2to3"}
            headingLevel={headingLevel}
            className={classNames('overflowmenu-in-use', {
                'job-object-custom': showNewJobLayout_V2,
            })}
        >
            {items.map((ad, index) => {
                if (ad.renderType === 'NATIVE_AD') {
                    return (
                        <NativeAd
                            // Prefixing with `position` ensurs that if the position changes then the ad is loaded again
                            key={`${position}-${
                                ad.itemId || `inject-${index}`
                            }`}
                            ad={ad}
                            trackAdClick={trackAdClick}
                            tracking={tracking}
                            position={position}
                        />
                    );
                }

                if (ad.renderType === 'EXTERNAL_AD') {
                    return (
                        <GoogleAd
                            // Prefixing with `position` ensurs that if the position changes then the ad is loaded again
                            key={`${position}-${
                                ad.itemId || `inject-${index}`
                            }`}
                            ad={ad}
                            trackAdClick={trackAdClick}
                            tracking={tracking}
                            position={position}
                        />
                    );
                }
                if (ad.renderType === 'BLINK') {
                    return (
                        <BlinkAd
                            // Prefixing with `position` ensurs that if the position changes then the ad is loaded again
                            key={`${position}-${
                                ad.itemId || `inject-${index}`
                            }`}
                            ad={ad}
                            trackAdClick={trackAdClick}
                            tracking={tracking}
                            position={position}
                        />
                    );
                }
                if (ad.renderType === 'CMS') {
                    return (
                        <CmsArticle
                            // Prefixing with `position` ensurs that if the position changes then the ad is loaded again
                            key={`${position}-${
                                ad.itemId || `inject-${index}`
                            }`}
                            ad={ad}
                            trackAdClick={trackAdClick}
                            tracking={tracking}
                            position={position}
                        />
                    );
                }
                // Make sure ad types that we don't know how to handle are excluded from the list
                if (!ad.itemId || (ad.renderType && ad.renderType !== 'ITEM')) {
                    console.error('Unable to render ad type', ad);
                    return null;
                }

                if (showNewJobLayout_V2) {
                    return (
                        <JobAd
                            key={ad.itemId}
                            tracking={tracking}
                            ad={ad}
                            rightActions={renderActions({
                                adId: ad.itemId,
                                itemType: 'Ad',
                            })}
                            trackAdClick={trackAdClick}
                            imagesCdnUrl={imagesCdnUrl}
                            logo={ad.overlay?.image?.url}
                            useOverflow={showOverflowMenu}
                            url={url}
                        />
                    );
                }

                return (
                    <StandardAd
                        key={ad.itemId}
                        product={product}
                        tracking={tracking}
                        ad={ad}
                        rightActions={renderActions({
                            adId: ad.favourite?.itemId,
                            itemType: ad.favourite?.itemType,
                        })}
                        trackAdClick={trackAdClick}
                        imagesCdnUrl={imagesCdnUrl}
                        logo={ad.overlay?.image?.url}
                        useOverflow={showOverflowMenu}
                        url={url}
                    />
                );
            })}
        </TroikaAds>
    );
};

export default Ads;
